
import React, { useState, useEffect } from 'react';
import MainLayout from '@/components/layout/MainLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/hooks/useAuth';
import { useLanguage } from '@/hooks/useLanguage';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Globe, Flag, Languages, Sun, Moon } from 'lucide-react';
import TranslationManager from '@/components/settings/TranslationManager';

const Settings = () => {
  const { user } = useAuth();
  const { language, setLanguage, translate } = useLanguage();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [darkMode, setDarkMode] = useState(false);

  // Check if dark mode is enabled from localStorage on component mount
  useEffect(() => {
    const isDarkMode = localStorage.getItem('darkMode') === 'true';
    setDarkMode(isDarkMode);
    
    // Apply dark mode class to html element
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, []);

  // Load user profile data
  useEffect(() => {
    const loadUserProfile = async () => {
      if (!user) return;
      
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('full_name, phone_number')
          .eq('id', user.id)
          .maybeSingle();
          
        if (error) {
          console.error('Error loading user profile:', error);
          return;
        }
        
        if (data) {
          // If we have a full name, try to split it into first and last name
          if (data.full_name) {
            const nameParts = data.full_name.split(' ');
            setFirstName(nameParts[0] || '');
            setLastName(nameParts.slice(1).join(' ') || '');
          }
          
          setPhoneNumber(data.phone_number || '');
        }
      } catch (error) {
        console.error('Failed to load user profile:', error);
      }
    };
    
    loadUserProfile();
  }, [user]);

  // Handle language change
  const handleLanguageChange = (value: string) => {
    setLanguage(value as 'en' | 'no');
    toast.success(translate('settings.languageUpdated'));
  };

  // Handle account save
  const handleSaveAccount = async () => {
    if (!user) return;
    
    try {
      const fullName = `${firstName} ${lastName}`.trim();
      
      const { error } = await supabase
        .from('profiles')
        .update({
          full_name: fullName,
          phone_number: phoneNumber,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);
      
      if (error) {
        console.error('Error saving user profile:', error);
        toast.error(error.message);
        return;
      }
      
      toast.success(translate('common.success'));
    } catch (error) {
      console.error('Failed to save user profile:', error);
      toast.error(translate('common.error'));
    }
  };

  // Handle dark mode toggle
  const handleDarkModeToggle = async (checked: boolean) => {
    setDarkMode(checked);
    localStorage.setItem('darkMode', checked ? 'true' : 'false');
    
    if (checked) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    
    // Save dark mode preference to user profile if logged in
    if (user) {
      try {
        const { error } = await supabase
          .from('profiles')
          .update({
            dark_mode: checked,
            updated_at: new Date().toISOString()
          })
          .eq('id', user.id);
          
        if (error) {
          console.error('Error saving dark mode preference:', error);
        }
      } catch (error) {
        console.error('Failed to save dark mode preference:', error);
      }
    }
    
    toast.success(translate('settings.darkModeUpdated'));
  };

  return (
    <MainLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">{translate('settings.title')}</h1>
          <p className="text-muted-foreground">
            {translate('settings.subtitle')}
          </p>
        </div>

        <Tabs defaultValue="account" className="space-y-4">
          <TabsList>
            <TabsTrigger value="account">
              {translate('settings.account')}
            </TabsTrigger>
            <TabsTrigger value="system">
              {translate('settings.system')}
            </TabsTrigger>
            <TabsTrigger value="translations">
              {translate('settings.translations')}
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="account" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>{translate('settings.accountInfo')}</CardTitle>
                <CardDescription>
                  {translate('settings.personalDetails')}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-1">
                  <Label>{translate('settings.email')}</Label>
                  <div className="rounded-md border px-4 py-3 font-mono text-sm">
                    {user?.email}
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-1">
                    <Label htmlFor="firstName">{translate('settings.firstName')}</Label>
                    <Input 
                      id="firstName" 
                      value={firstName} 
                      onChange={(e) => setFirstName(e.target.value)}
                      placeholder={translate('settings.firstName')}
                    />
                  </div>
                  
                  <div className="space-y-1">
                    <Label htmlFor="lastName">{translate('settings.lastName')}</Label>
                    <Input 
                      id="lastName" 
                      value={lastName} 
                      onChange={(e) => setLastName(e.target.value)}
                      placeholder={translate('settings.lastName')}
                    />
                  </div>
                </div>

                <div className="space-y-1">
                  <Label htmlFor="phoneNumber">{translate('settings.phoneNumber')}</Label>
                  <Input 
                    id="phoneNumber" 
                    value={phoneNumber} 
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    placeholder={translate('settings.phoneNumber')}
                  />
                </div>
                
                <Button onClick={handleSaveAccount} className="mt-4">
                  {translate('settings.saveChanges')}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="system" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>{translate('settings.systemSettings')}</CardTitle>
                <CardDescription>
                  {translate('settings.configureSystemSettings')}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <div className="flex items-center gap-2">
                      {darkMode ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
                      <Label htmlFor="dark-mode">{translate('settings.darkMode')}</Label>
                    </div>
                  </div>
                  <Switch 
                    id="dark-mode" 
                    checked={darkMode} 
                    onCheckedChange={handleDarkModeToggle}
                  />
                </div>
                
                <div className="space-y-1">
                  <Label htmlFor="language">{translate('settings.language')}</Label>
                  <Select value={language} onValueChange={handleLanguageChange}>
                    <SelectTrigger className="w-full md:w-[200px]">
                      <div className="flex items-center gap-2">
                        <Globe className="h-4 w-4" />
                        <SelectValue placeholder={translate('settings.selectLanguage')} />
                      </div>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="en" className="flex items-center">
                        <div className="flex items-center gap-2">
                          <Flag className="h-4 w-4" />
                          <span>English</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="no" className="flex items-center">
                        <div className="flex items-center gap-2">
                          <Flag className="h-4 w-4" />
                          <span>Norsk</span>
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="translations" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Languages className="h-5 w-5" />
                  {translate('translations.manageTranslations')}
                </CardTitle>
                <CardDescription>
                  {translate('translations.manageTranslationsDescription')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <TranslationManager />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </MainLayout>
  );
};

export default Settings;
